import { useCallback } from 'react'

import useAxios from 'hooks/useAxios'
import generateUserActivityLog from 'libs/generateUserActivityLog'

export const ACTION_NAME = {
  activate_autotask_customer_sync_integration: 'activate_autotask_customer_sync_integration',
  activate_autotask_update_custom_field: 'activate_autotask_update_custom_field',
  activate_connectwise_customer_sync_integration: 'activate_connectwise_customer_sync_integration',
  activate_connectwise_update_custom_field: 'activate_connectwise_update_custom_field',
  add_customer: 'add_customer',
  archive_survey: 'archive_survey',
  authenticate_autotask_credential: 'authenticate_autotask_credential',
  authenticate_connectwise_credential: 'authenticate_connectwise_credential',
  authenticated_intercom: 'authenticated_intercom',
  authenticate_zendesk_credential: 'authenticate_zendesk_credential',
  clicked_file_upload_upgrade_cta: 'clicked_file_upload_upgrade_cta',
  connected_intercom: 'connected_intercom',
  completed_wizard: 'completed_wizard',
  created_survey: 'created_survey',
  create_widget: 'create_widget',
  deactivate_autotask_customer_sync_integration: 'deactivate_autotask_customer_sync_integration',
  deactivate_autotask_update_custom_field: 'deactivate_autotask_update_custom_field',
  deactivate_connectwise_customer_sync_integration:
    'deactivate_connectwise_customer_sync_integration',
  deactivate_connectwise_update_custom_field: 'deactivate_connectwise_update_custom_field',
  delete_customer: 'delete_customer',
  delete_user: 'delete_user',
  delete_widget: 'delete_widget',
  deleted_survey: 'deleted_survey',
  duplicate_survey: 'duplicate_survey',
  edit_customer: 'edit_customer',
  edit_intercom_push_contact_custom_field: 'edit_intercom_push_contact_custom_field',
  edit_sso_configuration: 'edit_sso_configuration',
  edit_survey_access: 'edit_survey_access',
  edit_survey_configuration: 'edit_survey_configuration',
  edit_survey_publish_delivery_page: 'edit_survey_publish_delivery_page',
  edit_survey_publish_email_page: 'edit_survey_publish_email_page',
  edit_user_role_and_survey_access: 'edit_user_role_and_survey_access',
  edit_widget: 'edit_widget',
  export_customer: 'export_customer',
  time_until_expiration: 'time_until_expiration',
  expiration_unit: 'expiration_unit',
  is_expiration_enabled: 'is_expiration_enabled',
  remove_from_workspace: 'remove_from_workspace',
  restored_survey: 'restored_survey',
  revoke_autotask_credential: 'revoke_autotask_credential',
  revoke_connectwise_credential: 'revoke_connectwise_credential',
  revoke_intercom_credential: 'revoke_intercom_credential',
  revoke_zendesk_credential: 'revoke_zendesk_credential',
  signed_in: 'signed_in',
  subscribe_email: 'subscribe_email',
  toggle_follow_up_email_survey: 'toggle_follow_up_email_survey',
  trigger_import_customer: 'trigger_import_customer',
  unsubscribe_email: 'unsubscribe_email',
  update_autotask_custom_field: 'update_autotask_custom_field',
  update_connectwise_custom_field: 'update_connectwise_custom_field',
  update_intercom_ticket_custom_field: 'update_intercom_ticket_custom_field',
  updated_company_profile: 'updated_company_profile',
  updated_survey: 'updated_survey',
  updated_user_profile: 'updated_user_profile',
  viewed_file_upload_upgrade_cta: 'viewed_file_upload_upgrade_cta',
}

export const SURVEY_STATUS_ACTION_NAME = {
  active: ACTION_NAME.restored_survey,
  archived: ACTION_NAME.archive_survey,
  deleted: ACTION_NAME.deleted_survey,
}

export const ACCOUNT_ACTION_NAME = {
  authenticated_email_domain: 'authenticated_email_domain',
  configured_sso: 'configured_sso',
  created_role: 'created_role',
  saved_brand: 'saved_brand',
  invited_user: 'invited_user',
}

export const CUSTOM_DASHBOARD_ACTION_NAME = {
  archived_dashboard: 'archived_dashboard',
  created_dashboard: 'created_dashboard',
  created_dashboard_widget: 'created_dashboard_widget',
  deleted_dashboard: 'deleted_dashboard',
  deleted_dashboard_widget: 'deleted_dashboard_widget',
  saved_dashboard_filter: 'saved_dashboard_filter',
  updated_dashboard: 'updated_dashboard',
  updated_dashboard_widget: 'updated_dashboard_widget',
}

export const FEEDBACK_VIEW_ACTION_NAME = {
  bulk_deleted_responses: 'bulk_deleted_responses',
  created_feedback_view: 'created_feedback_view',
  deleted_feedback_view: 'deleted_feedback_view',
  deleted_response: 'deleted_response',
  imported_feedback: 'imported_feedback',
  published_answer: 'published_answer',
  scheduled_feedback_report: 'scheduled_feedback_report',
  updated_feedback_view: 'updated_feedback_view',
}

export const INTEGRATION_ACTION_NAME = {
  authenticated_integration: 'authenticated_integration',
  connected_integration: 'connected_integration',
  setup_microsoft_team: 'setup_microsoft_team',
  setup_slack_notification: 'setup_slack_notification',
}

export const SURVEY_VIEW_ACTION_NAME = {
  created_survey_view: 'created_survey_view',
  deleted_survey_view: 'deleted_survey_view',
  updated_survey_view: 'updated_survey_view',
}

export const REPORT_VIEW_ACTION_NAME = {
  created_view: 'created_report_view',
  deleted_view: 'deleted_report_view',
  scheduled_reporting_report: 'scheduled_reporting_report',
  updated_view: 'updated_report_view',
}

export const ANSWER_TOPIC_ACTION_NAME = {
  activate_topic: 'activate_topic',
  add_topic_to_answer: 'add_topic_to_answer',
  create_new_topic: 'create_new_topic',
  create_new_topic_group: 'create_new_topic_group',
  deactivate_topic: 'deactivate_topic',
  delete_topic: 'delete_topic',
  delete_topic_group: 'delete_topic_group',
  edit_topic: 'edit_topic',
  edit_topic_group: 'edit_topic_group',
  remove_topic_from_answer: 'remove_topic_from_answer',
}

export const API_KEY_ACTION_NAME = {
  created_api_key: 'created_api_key',
  deleted_api_key: 'deleted_api_key',
  updated_api_key: 'updated_api_key',
}

export const TEAM_MEMBER_ACTION_NAME = {
  created_team_member: 'created_team_member',
  updated_team_member: 'updated_team_member',
  deleted_team_member: 'deleted_team_member',
  merged_team_member: 'merged_team_member',
  created_team_member_group: 'created_team_member_group',
  updated_team_member_group: 'updated_team_member_group',
  deleted_team_member_group: 'deleted_team_member_group',
  mapped_team_member: 'mapped_team_member',
}

export default function useSaveUserActivityLog() {
  const { fetch: postUserActivityLog } = useAxios({
    method: 'post',
    url: `/user-activity-log/write/`,
    skip: true,
  })

  const saveUserActivityLog = useCallback(
    /**
     * @typedef {object} UserActivityLog
     * @property {keyof typeof ACTION_NAME & typeof CUSTOM_DASHBOARD_ACTION_NAME & typeof REPORT_VIEW_ACTION_NAME & typeof ANSWER_TOPIC_ACTION_NAME | string} action
     * @property {string} target
     * @property {object=} requestBody
     * @property {object=} errorResponseBody
     * @property {number} statusCode
     * @property {object=} headers
     *
     * @param {UserActivityLog} log
     */
    ({ action, target, requestBody, errorResponseBody, statusCode, headers }) => {
      const request = generateUserActivityLog({
        action,
        target,
        requestBody,
        errorResponseBody,
        statusCode,
      })
      postUserActivityLog({ data: request, headers: { ...headers } })
    },
    [postUserActivityLog]
  )

  return saveUserActivityLog
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/hooks/useSaveUserActivityLog/index.js